import { ChevronDown, ChevronRight, Loader2, Plus } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { ChatToolCallState, MessageStatus } from '@/modules/common/types';

import { useConversation } from '../application/use-conversation';
import { ConversationStatus, type EnhancedConversation } from '../types/conversation';
import { formatDistanceToNow } from '../utils/distance-to-now';

const DAY_IN_MS = 24 * 60 * 60 * 1000;

type ConversationGroupKey = 'today' | 'last7Days' | 'older';

function getConversationTimestamp(conversation: EnhancedConversation) {
  const value = conversation.lastMessage?.createdAt ?? conversation.updatedAt;
  const time = new Date(value).getTime();

  return Number.isFinite(time) ? time : 0;
}

function getConversationGroup(conversation: EnhancedConversation, now = new Date()) {
  const timestamp = getConversationTimestamp(conversation);
  const todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();

  if (timestamp >= todayStart) {
    return 'today';
  }

  if (timestamp >= now.getTime() - 7 * DAY_IN_MS) {
    return 'last7Days';
  }

  return 'older';
}

function getConversationTitle(conversation: EnhancedConversation, fallback: string) {
  const title = conversation.title || conversation.lastMessage?.content || fallback;

  return (
    title
      .replace(/[*_`#]/g, '')
      .replace(/\s+/g, ' ')
      .trim() || fallback
  );
}

function hasPendingApproval(conversation: EnhancedConversation) {
  return (
    conversation.lastMessage?.toolCalls?.some(
      (toolCall) => toolCall.state === ChatToolCallState.APPROVAL_REQUESTED
    ) ?? false
  );
}

function isRunningConversation(conversation: EnhancedConversation) {
  return (
    conversation.status === ConversationStatus.PROCESSING ||
    conversation.lastMessage?.status === MessageStatus.IN_PROGRESS
  );
}

function ConversationTrailingIndicator({ conversation }: { conversation: EnhancedConversation }) {
  const { t } = useTranslation();

  if (hasPendingApproval(conversation)) {
    return (
      <Badge
        variant="outline"
        className="h-5 rounded-full border-amber-200 bg-amber-50 px-2 text-[11px] font-medium text-amber-700"
      >
        {t('modules.mavue2.chat.historyApprovalRequired')}
      </Badge>
    );
  }

  if (isRunningConversation(conversation)) {
    return <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />;
  }

  const timestamp = getConversationTimestamp(conversation);

  return (
    <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
      {timestamp ? formatDistanceToNow(new Date(timestamp)) : null}
    </span>
  );
}

function ConversationRow({ conversation }: { conversation: EnhancedConversation }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { conversationId } = useParams();
  const isActive = conversation.id === conversationId;
  const title = getConversationTitle(conversation, t('modules.mavue2.chat.untitledConversation'));

  return (
    <button
      type="button"
      onClick={() => navigate(`/chat/${conversation.id}`)}
      className={cn(
        'grid h-8 w-full min-w-0 grid-cols-[minmax(0,1fr)_auto] items-center gap-2 rounded-lg px-3 text-left transition-colors',
        isActive
          ? 'bg-sidebar-accent text-sidebar-accent-foreground'
          : 'hover:bg-sidebar-accent/70 hover:text-sidebar-accent-foreground'
      )}
    >
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="min-w-0 truncate text-[13px] font-medium leading-5">{title}</span>
        </TooltipTrigger>
        <TooltipContent side="right" align="start" className="max-w-80">
          {title}
        </TooltipContent>
      </Tooltip>
      <span className="flex min-w-8 shrink-0 items-center justify-end">
        <ConversationTrailingIndicator conversation={conversation} />
      </span>
    </button>
  );
}

function ConversationSection({
  label,
  conversations,
  emptyLabel,
}: {
  label: string;
  conversations: EnhancedConversation[];
  emptyLabel: string;
}) {
  const [isOpen, setIsOpen] = useState(true);

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen} className="space-y-1.5">
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="flex w-full items-center gap-1 px-1 text-left text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground transition-colors hover:text-sidebar-foreground"
        >
          <span>{label}</span>
          {isOpen ? (
            <ChevronDown className="h-3.5 w-3.5" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5" />
          )}
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="space-y-0.5">
          {conversations.length > 0 ? (
            conversations.map((conversation) => (
              <ConversationRow key={conversation.id} conversation={conversation} />
            ))
          ) : (
            <div className="rounded-lg px-3 py-2 text-[13px] leading-5 text-muted-foreground/70">
              {emptyLabel}
            </div>
          )}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}

export function ChatHistorySidebar() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { conversations, conversationsLoading } = useConversation();

  const groups = conversations.reduce<Record<ConversationGroupKey, EnhancedConversation[]>>(
    (result, conversation) => {
      result[getConversationGroup(conversation)].push(conversation);
      return result;
    },
    { today: [], last7Days: [], older: [] }
  );

  return (
    <TooltipProvider delayDuration={700}>
      <div className="flex min-h-0 flex-1 flex-col px-3 pb-2 group-data-[collapsible=icon]:hidden">
        <div className="pb-2 pt-1">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/chat')}
            className="h-10 w-full justify-start gap-2 rounded-xl border-sidebar-border bg-background shadow-none"
          >
            <Plus className="h-4 w-4" />
            {t('modules.mavue2.navigation.newChat')}
          </Button>
        </div>

        {conversationsLoading ? (
          <div className="flex flex-1 items-center justify-center text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : (
          <ScrollArea className="-mx-3 flex-1 px-3">
            <div className="space-y-4 pb-4 pt-2">
              <ConversationSection
                label={t('modules.mavue2.chat.historyGroups.today')}
                conversations={groups.today}
                emptyLabel={t('modules.mavue2.chat.historyEmptyGroup')}
              />
              <ConversationSection
                label={t('modules.mavue2.chat.historyGroups.last7Days')}
                conversations={groups.last7Days}
                emptyLabel={t('modules.mavue2.chat.historyEmptyGroup')}
              />
              <ConversationSection
                label={t('modules.mavue2.chat.historyGroups.older')}
                conversations={groups.older}
                emptyLabel={t('modules.mavue2.chat.historyEmptyGroup')}
              />
            </div>
          </ScrollArea>
        )}
      </div>
    </TooltipProvider>
  );
}
